import { Component, computed, input } from '@angular/core';

export interface BarChartColumn {
    key: string;
    label: string;
    color?: string;
}

export interface BarChartCellValue {
    value: number;
    color?: string;
}

export interface BarChartRow {
    label: string;
    values: Record<string, number | BarChartCellValue>;
    isTotal?: boolean;
}

export enum BarChartVariantEnum {
    TABLE = 'table',
    STACKED = 'stacked',
}

interface ComputedBarChartCell {
    key: string;
    value: number;
    percentage: number;
    formattedPercentage: string;
    color: string;
}

interface ComputedBarChartRow {
    label: string;
    isTotal: boolean;
    cells: ComputedBarChartCell[];
}

@Component({
    selector: 'app-ice-visuals-dashboard-bar-chart',
    imports: [],
    templateUrl: './ice-visuals-dashboard-bar-chart.component.html',
    styleUrl: './ice-visuals-dashboard-bar-chart.component.scss',
})
export class IceVisualsDashboardBarChartComponent {
    columns = input.required<BarChartColumn[]>();
    rows = input.required<BarChartRow[]>();
    variant = input<BarChartVariantEnum>(BarChartVariantEnum.TABLE);
    loading = input<boolean>(false);
    skeletonRowCount = input<number>(5);
    rowGap = input<number>(20);
    maxValue = input<number | null>(null);

    barChartVariantEnum = BarChartVariantEnum;

    private static readonly DEFAULT_BAR_COLOR = '#6b7280';

    computedRows = computed<ComputedBarChartRow[]>(() => {
        const columnDefinitions = this.columns();
        const rowData = this.rows();
        const fixedMax = this.maxValue();

        const resolvedValues = rowData.map((row) =>
            columnDefinitions.map((column) => {
                const rawValue = row.values[column.key];
                if (rawValue === undefined || rawValue === null) {
                    return { value: 0, color: undefined };
                }
                if (typeof rawValue === 'number') {
                    return { value: rawValue, color: undefined };
                }
                return { value: rawValue.value, color: rawValue.color };
            }),
        );

        const maxPerColumn = columnDefinitions.map((_, columnIndex) => {
            if (fixedMax !== null) {
                return fixedMax;
            }
            let columnMax = 0;
            for (const rowValues of resolvedValues) {
                if (rowValues[columnIndex].value > columnMax) {
                    columnMax = rowValues[columnIndex].value;
                }
            }
            return columnMax;
        });

        return rowData.map((row, rowIndex) => ({
            label: row.label,
            isTotal: row.isTotal ?? false,
            cells: columnDefinitions.map((column, columnIndex) => {
                const resolved = resolvedValues[rowIndex][columnIndex];
                const columnMax = maxPerColumn[columnIndex];
                const barPercentage = columnMax > 0 ? (resolved.value / columnMax) * 100 : 0;
                const color = resolved.color ?? column.color ?? IceVisualsDashboardBarChartComponent.DEFAULT_BAR_COLOR;

                return {
                    key: column.key,
                    value: resolved.value,
                    percentage: barPercentage,
                    formattedPercentage: `${Math.round(resolved.value)}%`,
                    color,
                };
            }),
        }));
    });

    computedColumns = computed(() => this.columns());

    skeletonRows = computed(() => Array.from({ length: this.skeletonRowCount() }, (_, index) => index));

    skeletonColumns = computed(() => this.columns());
}
